import { Member } from '@scrambl/shared';

// Mention types
export interface MentionUser {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

export interface Mention {
  id: string;
  userId: string;
  name: string;
  startIndex: number;
  endIndex: number;
}

export interface MentionMatch {
  text: string;
  index: number;
  length: number;
  userId?: string;
  userName?: string;
}

// Convert Member to MentionUser
export const memberToMentionUser = (member: Member): MentionUser => ({
  id: member.userId,
  name: member.user.displayName || 
        `${member.user.firstName || ''} ${member.user.lastName || ''}`.trim() || 
        member.user.email,
  email: member.user.email,
  avatarUrl: member.user.image || undefined,
});

// Mention detection patterns
export const MENTION_PATTERN = /@\[([^\]]+)\]\(([a-f0-9-]{36})\)/g; // @[John Doe](uuid)
export const MENTION_TRIGGER_PATTERN = /@(\w*)$/; // @ followed by optional word characters at end of input

// Extract mentions from text
export const extractMentions = (text: string): Mention[] => {
  const mentions: Mention[] = [];
  let match;
  
  // Reset regex lastIndex to ensure we start from the beginning
  MENTION_PATTERN.lastIndex = 0;
  
  while ((match = MENTION_PATTERN.exec(text)) !== null) {
    mentions.push({
      id: `mention-${match.index}`,
      userId: match[2], // UUID from the pattern
      name: match[1], // Display name from the pattern
      startIndex: match.index,
      endIndex: match.index + match[0].length
    });
  }
  
  return mentions;
};

// Convert mention notation to display text
export const mentionToDisplayText = (text: string): string => {
  return text.replace(MENTION_PATTERN, '@$1');
};

// Convert mention notation to HTML for display
export const mentionToHtml = (text: string): string => {
  const escapeHtml = (value: string): string =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  return text.replace(MENTION_PATTERN, (
    match: string, 
    name: string, 
    userId: string
  ) => {
    const safeName = escapeHtml(name);
    const safeUserId = escapeHtml(userId);
    return `<span class="mention inline-flex items-center px-1.5 py-0.5 rounded-md bg-primary/10 text-primary font-medium hover:bg-primary/20 transition-colors cursor-pointer" data-user-id="${safeUserId}" data-user-name="${safeName}" title="@${safeName}" style="font-size: inherit; line-height: inherit;">@${safeName}</span>`;
  });
};

// Find mention trigger position in text
export const findMentionTrigger = (text: string, cursorPosition: number): { 
  triggerIndex: number; 
  searchText: string; 
} | null => {
  const textBeforeCursor = text.substring(0, cursorPosition);
  const match = textBeforeCursor.match(MENTION_TRIGGER_PATTERN);
  
  
  if (match) {
    return {
      triggerIndex: match.index!,
      searchText: match[1] || ''
    };
  }
  
  return null;
};

// Replace mention trigger with mention notation
export const replaceMentionTrigger = (
  text: string,
  triggerIndex: number,
  searchText: string,
  user: MentionUser
): string => {
  const beforeTrigger = text.substring(0, triggerIndex);
  const afterTrigger = text.substring(triggerIndex + searchText.length + 1); // +1 for @
  
  return `${beforeTrigger}@[${user.name}](${user.id})${afterTrigger}`;
};

// Filter users based on search text
export const filterUsersForMention = (
  users: MentionUser[],
  searchText: string
): MentionUser[] => {
  if (!searchText) {
    return users.slice(0, 10); // Show first 10 users when no search
  }
  
  const searchLower = searchText.toLowerCase();
  
  return users
    .filter(user => 
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    )
    .slice(0, 10); // Limit to 10 results
};

// Check if text contains mentions
export const hasMentions = (text: string): boolean => {
  MENTION_PATTERN.lastIndex = 0;
  return MENTION_PATTERN.test(text);
};

// Get mentioned user IDs from text
export const getMentionedUserIds = (text: string): string[] => {
  const mentions = extractMentions(text);
  return mentions.map(mention => mention.userId);
};

// Extract user IDs from HTML mentions (for rich text editors)
export const getMentionedUserIdsFromHtml = (html: string): string[] => {
  const userIds: string[] = [];
  
  // Pattern for custom mention spans with data-user-id
  const customMentionRegex = /<span[^>]*data-user-id="([^"]+)"[^>]*>/g;
  let match;
  
  while ((match = customMentionRegex.exec(html)) !== null) {
    const userId = match[1];
    if (userId && !userIds.includes(userId)) {
      userIds.push(userId);
    }
  }
  
  // Pattern for quill-mention format: <span class="mention" data-index="0" data-denotation-char="@" data-id="user-id" data-value="Name">@Name</span>
  const quillMentionRegex = /<span[^>]*class="[^"]*mention[^"]*"[^>]*data-id="([^"]+)"[^>]*>/g;
  quillMentionRegex.lastIndex = 0; // Reset regex state
  
  while ((match = quillMentionRegex.exec(html)) !== null) {
    const userId = match[1];
    if (userId && !userIds.includes(userId)) {
      userIds.push(userId);
    }
  }
  
  return userIds;
};

// Validate mention format
export const isValidMention = (text: string): boolean => {
  MENTION_PATTERN.lastIndex = 0;
  const match = MENTION_PATTERN.exec(text);
  return match !== null && match[0] === text;
};

// Convert display text with mentions back to stored format
export const displayTextToStoredText = (
  displayText: string, 
  previousStoredText: string
): string => {
  // If display text has no @ symbols, return as-is
  if (!displayText.includes('@')) {
    return displayText;
  }
  
  // Extract existing mentions from stored text to maintain their IDs
  const existingMentions = extractMentions(previousStoredText);
  const mentionMap = new Map<string, string>(); // @Name -> @[Name](id)
  
  existingMentions.forEach(mention => {
    mentionMap.set(`@${mention.name}`, `@[${mention.name}](${mention.userId})`);
  });
  
  // If we have no existing mentions to map, just return the display text
  // This handles the case where user is typing new mentions
  if (mentionMap.size === 0) {
    return displayText;
  }
  
  // Replace @Name patterns with their stored format if we have the mapping
  let result = displayText;
  
  // Sort by length (longest first) to avoid partial replacements
  const sortedEntries = Array.from(mentionMap.entries()).sort((a, b) => b[0].length - a[0].length);
  
  sortedEntries.forEach(([displayFormat, storedFormat]) => {
    // Use a more flexible regex that doesn't require word boundaries
    // This allows text to continue after mentions
    const regex = new RegExp(escapeRegExp(displayFormat), 'g');
    result = result.replace(regex, storedFormat);
  });
  
  return result;
};

// Escape special regex characters
const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

// Clean text by removing mention markup (for plain text display)
export const cleanMentionText = (text: string): string => {
  return text.replace(MENTION_PATTERN, '@$1');
};
